import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  getCustomerPortalUrl,
  resetCustomerPortalToken,
  getPortalBaseUrl
} from '../utils/customerPortal';
import { buildPortalUrl } from '../utils/portalToken';

const Panel = styled.div`
  margin-top: 24px;
  padding: 16px;
  border: 1px solid rgba(var(--al-primary-rgb), 0.25);
  border-radius: 8px;
  background: rgba(var(--al-primary-rgb), 0.05);
`;

const Title = styled.h3`
  margin: 0 0 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--al-primary);
`;

const UrlRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  margin-bottom: 12px;
`;

const UrlInput = styled.input`
  flex: 1;
  padding: 8px 10px;
  font-size: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  background: var(--al-bg);
  color: rgba(255, 255, 255, 0.85);
  font-family: monospace;
`;

const Btn = styled.button`
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  background: ${(p) => (p.$danger ? '#ff6b6b' : 'var(--al-primary)')};
  color: ${(p) => (p.$danger ? '#fff' : 'var(--al-bg)')};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
`;

const Message = styled.p`
  margin: 8px 0 0;
  font-size: 0.8rem;
  color: ${(p) => (p.$error ? '#ff6b6b' : 'var(--al-primary)')};
`;

function CustomerPortalPanel({ customer, onUpdated }) {
  const [portalUrl, setPortalUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [portalEnabled, setPortalEnabled] = useState(customer?.portalEnabled !== false);

  useEffect(() => {
    setPortalEnabled(customer?.portalEnabled !== false);
    let cancelled = false;

    (async () => {
      if (!customer?.id) {
        setPortalUrl('');
        return;
      }
      try {
        const url = await getCustomerPortalUrl(db, customer);
        if (!cancelled) setPortalUrl(url);
      } catch (error) {
        console.error(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customer]);

  const handleCopy = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      setMessage('Link copied.');
    } catch {
      setMessage('Could not copy link.');
    }
  };

  const handleReset = async () => {
    if (!customer?.id) return;
    if (!window.confirm('Reset portal link? The old link will stop working.')) return;

    setLoading(true);
    setMessage('');
    try {
      const newToken = await resetCustomerPortalToken(db, customer.id);
      const base = await getPortalBaseUrl(db);
      setPortalUrl(buildPortalUrl(base, newToken));
      setMessage('New portal link generated.');
      onUpdated?.();
    } catch (error) {
      setMessage(error.message || 'Reset failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!customer?.id) return;
    const next = !portalEnabled;
    setLoading(true);
    setMessage('');
    try {
      await updateDoc(doc(db, 'customers', customer.id), { portalEnabled: next });
      setPortalEnabled(next);
      setMessage(next ? 'Portal enabled.' : 'Portal disabled.');
      onUpdated?.();
    } catch (error) {
      setMessage(error.message || 'Update failed.');
    } finally {
      setLoading(false);
    }
  };

  if (!customer?.id) return null;

  return (
    <Panel>
      <Title>Customer portal</Title>
      <ToggleLabel>
        <input
          type="checkbox"
          checked={portalEnabled}
          onChange={handleToggleEnabled}
          disabled={loading}
        />
        Portal access enabled
      </ToggleLabel>
      <UrlRow>
        <UrlInput readOnly value={portalUrl || 'Generating link…'} />
        <Btn type="button" onClick={handleCopy} disabled={!portalUrl || loading}>
          Copy
        </Btn>
      </UrlRow>
      <BtnRow>
        <Btn type="button" $danger onClick={handleReset} disabled={loading}>
          Reset link
        </Btn>
      </BtnRow>
      {message && <Message $error={message.includes('fail') || message.includes('Could not')}>{message}</Message>}
    </Panel>
  );
}

export default CustomerPortalPanel;
